import { Matrix, Quaternion, Vector3 } from '@babylonjs/core/Maths/math.vector';
import { Mesh } from '@babylonjs/core/Meshes/mesh';
import { Scene } from '@babylonjs/core/scene';
import { BoundingBox, Font } from 'opentype.js';
import { earcut } from './earcut';
import { SubmeshVertexData } from './geometry-helper';

const MAX_BEZIER_STEPS = 10;
const BEZIER_STEP_SIZE = 20.0;
const GLYPH_COORDS_SCALE = 0.0005;
const IDENTITY_Q = Quaternion.Identity();

interface Point {
    x: number;
    y: number;
}

interface GlyphDef {
    index: number;
    advanceWidth: number;
    boundingBox: BoundingBox;
    vertexData?: SubmeshVertexData;
}

// class for converting path commands into point data
class TextMeshPolygon {
    points: Point[] = [];
    children: TextMeshPolygon[] = [];
    area = 0.0;

    distance(p1: Point, p2: Point) {
        const dx = p1.x - p2.x,
            dy = p1.y - p2.y;
        return Math.sqrt(dx * dx + dy * dy);
    }

    lerp(p1: Point, p2: Point, t: number) {
        return { x: (1 - t) * p1.x + t * p2.x, y: (1 - t) * p1.y + t * p2.y };
    }

    cross(p1: Point, p2: Point) {
        return p1.x * p2.y - p1.y * p2.x;
    }

    moveTo(p: Point) {
        this.points.push(p);
    }

    lineTo(p: Point) {
        this.points.push(p);
    }

    close() {
        let cur = this.points[this.points.length - 1];

        this.points.forEach(next => {
            this.area += 0.5 * this.cross(cur, next);
            cur = next;
        });
    }

    conicTo(p: Point, p1: Point) {
        const p0 = this.points[this.points.length - 1];
        const dist = this.distance(p0, p1) + this.distance(p1, p);
        const steps = Math.max(2, Math.min(MAX_BEZIER_STEPS, dist / BEZIER_STEP_SIZE));

        for (let i = 1; i <= steps; ++i) {
            const t = i / steps;
            this.points.push(this.lerp(this.lerp(p0, p1, t), this.lerp(p1, p, t), t));
        }
    }

    cubicTo(p: Point, p1: Point, p2: Point) {
        const p0 = this.points[this.points.length - 1];
        const dist = this.distance(p0, p1) + this.distance(p1, p2) + this.distance(p2, p);
        const steps = Math.max(2, Math.min(MAX_BEZIER_STEPS, dist / BEZIER_STEP_SIZE));

        for (let i = 1; i <= steps; ++i) {
            const t = i / steps;
            const a = this.lerp(this.lerp(p0, p1, t), this.lerp(p1, p2, t), t);
            const b = this.lerp(this.lerp(p1, p2, t), this.lerp(p2, p, t), t);
            this.points.push(this.lerp(a, b, t));
        }
    }

    inside(p: Point) {
        const epsilon = 1e-6;
        let count = 0,
            cur = this.points[this.points.length - 1];

        this.points.forEach(next => {
            const p0 = cur.y < next.y ? cur : next;
            const p1 = cur.y < next.y ? next : cur;

            if (p0.y < p.y + epsilon && p1.y > p.y + epsilon) {
                if ((p1.x - p0.x) * (p.y - p0.y) > (p.x - p0.x) * (p1.y - p0.y)) {
                    count++;
                }
            }

            cur = next;
        });
        return count % 2 !== 0;
    }
}

export class TextMeshFont {
    glyphsParent: Mesh;
    glyphs: Record<string, GlyphDef> = {};

    constructor(name: string, public font: Font, private scene: Scene) {
        this.font = font;
        this.glyphsParent = new Mesh(name, scene);
    }

    createGlyph(ch: string) {
        if (this.glyphs[ch]) {
            return this.glyphs[ch];
        }

        const glyph = this.font.charToGlyph(ch);

        if (!glyph || !glyph.advanceWidth) {
            return undefined;
        }
        this.glyphs[ch] = {
            index: glyph.index,
            advanceWidth: glyph.advanceWidth,
            boundingBox: glyph.getBoundingBox(),
        };
        const glyphDef = this.glyphs[ch];

        if (!glyph.path || !glyph.path.commands || !glyph.path.commands.length) {
            return glyphDef;
        }
        const polys: TextMeshPolygon[] = [];
        glyph.path.commands.forEach(command => {
            const { type, x, y, x1, y1, x2, y2 } = command as unknown as Record<string, number> & { type: string };
            switch (type) {
                case 'M':
                    polys.push(new TextMeshPolygon());
                    polys[polys.length - 1].moveTo({ x, y });
                    break;
                case 'L':
                    polys[polys.length - 1].moveTo({ x, y });
                    break;
                case 'C':
                    polys[polys.length - 1].cubicTo({ x, y }, { x: x1, y: y1 }, { x: x2, y: y2 });
                    break;
                case 'Q':
                    polys[polys.length - 1].conicTo({ x, y }, { x: x1, y: y1 });
                    break;
                case 'Z':
                    polys[polys.length - 1].close();
                    break;
            }
        });

        // sort contours by descending area
        polys.sort((a, b) => Math.abs(b.area) - Math.abs(a.area));

        // classify contours to find holes and their 'parents'
        const root = [];

        for (let i = 0; i < polys.length; ++i) {
            let parent: TextMeshPolygon | null = null;
            for (let j = i - 1; j >= 0; --j) {
                // a contour is a hole if it is inside its parent and has different winding
                if (polys[j].inside(polys[i].points[0]) && polys[i].area * polys[j].area < 0) {
                    parent = polys[j];
                    break;
                }
            }
            if (parent) {
                parent.children.push(polys[i]);
            } else {
                root.push(polys[i]);
            }
        }

        const totalPoints = polys.reduce((sum, p) => sum + p.points.length, 0);
        const vertexData = new Float32Array(totalPoints * 2);
        let vertexCount = 0;
        let pathSegmentCount = 0;
        const indices: number[] = [];
        const paths: number[] = [];
        const holePaths: number[] = [];

        function process(poly: TextMeshPolygon) {
            // construct input for earcut
            const coords: number[] = [];
            const holes: number[] = [];
            const n = poly.points.length - 1;
            for (let i = 0; i < n; ++i) {
                const { x, y } = poly.points[n - i];
                if (i > 0) {
                    const idx = coords.length / 2;
                    paths.push(vertexCount + idx - 1, vertexCount + idx);
                }
                coords.push(x, y);
            }
            const parentSegmentIdx = pathSegmentCount;
            poly.children.forEach((child, i) => {
                // children's children are new, separate shapes
                child.children.forEach(process);

                // idx 0 of a hole is the segment index of the segment the hole is inside. each separate poly is a separate segment
                holePaths.push(parentSegmentIdx, paths.length);
                holes.push(coords.length / 2);
                const n = child.points.length - 1;
                for (let i = 0; i < n; ++i) {
                    const { x, y } = child.points[i];
                    if (i > 0) {
                        const idx = coords.length / 2;
                        paths.push(idx - 1, idx);
                    }
                    coords.push(x, y);
                }
                pathSegmentCount++;
            });

            // add vertex data
            vertexData.set(coords, vertexCount * 2);
            // add index data
            earcut(coords, holes).forEach(i => {
                indices.push(i + vertexCount);
            });
            vertexCount += coords.length / 2;
            pathSegmentCount++;
        }

        root.forEach(process);

        const meshdata = new SubmeshVertexData();
        const vertices: number[] = [];
        const normals: number[] = [];

        for (let i = 0; i < vertexCount; i++) {
            vertices.push(vertexData[i * 2] * GLYPH_COORDS_SCALE);
            vertices.push(vertexData[i * 2 + 1] * GLYPH_COORDS_SCALE);
            vertices.push(0);

            normals.push(0);
            normals.push(0);
            normals.push(1);
        }

        const flipIndices = [];
        for (let i = 0; i < indices.length; i += 3) {
            flipIndices.push(indices[i], indices[i + 2], indices[i + 1]);
        }

        meshdata.positions = vertices;
        meshdata.indices = flipIndices;
        meshdata.normals = normals;
        meshdata.paths = paths;
        meshdata.holes = holePaths;

        glyphDef.vertexData = meshdata;

        return glyphDef;
    }
}

export class TextMeshVertexData {
    instances: Record<string, GlyphDef> = {};

    constructor(private textMeshFont: TextMeshFont) {}

    getText(text: string, fontSize = 1, charSpacing = 1, radius = 0) {
        radius = radius / fontSize;
        const scaleMatrix = Matrix.Compose(new Vector3(fontSize, fontSize, 1), IDENTITY_Q, Vector3.ZeroReadOnly);
        const vertexData = new SubmeshVertexData();
        const pos = Vector3.Zero();
        const transformMatrix = Matrix.Identity();

        const generatedVertices: Array<{ data: SubmeshVertexData; transform: Matrix; center: number }> = [];

        for (let i = 0; i < text.length; i++) {
            const ch1 = text[i];

            if (ch1 === '\n') {
                pos.x = 0;
                pos.y -= 1.1;
                continue;
            }
            transformMatrix.setTranslation(pos);

            const ch2 = text[i + 1];
            const g = this.textMeshFont.createGlyph(ch1);

            if (!g) {
                continue;
            }
            let advance = g.advanceWidth;

            if (g.vertexData) {
                generatedVertices.push({
                    data: g.vertexData.deepClone(),
                    transform: transformMatrix.clone(),
                    center: (advance / 2 + g.boundingBox.x1) * GLYPH_COORDS_SCALE,
                });
            }

            if (advance) {
                if (ch2 && this.textMeshFont.glyphs[ch2]) {
                    const kern = this.textMeshFont.font.getKerningValue(g.index, this.textMeshFont.glyphs[ch2].index);

                    if (kern) {
                        advance += kern;
                    }
                }

                pos.x += (advance - g.boundingBox.x2 + g.boundingBox.x2 * charSpacing) * GLYPH_COORDS_SCALE;
            }
        }

        if (generatedVertices.length > 0) {
            const centerTransform = Matrix.Compose(Vector3.OneReadOnly, IDENTITY_Q, pos.scale(-0.5));

            for (const generatedVertexData of generatedVertices) {
                const transform = generatedVertexData.transform;
                transform.multiplyToRef(centerTransform, transform);

                // Add curve to text
                if (radius !== 0) {
                    const baseOffset = transform.getTranslation();
                    const angle = (baseOffset.x + generatedVertexData.center) / radius;
                    const pivotMatrix = Matrix.Compose(
                        Vector3.OneReadOnly,
                        IDENTITY_Q,
                        new Vector3(generatedVertexData.center, -radius, 0)
                    );
                    const rotMatrix = Matrix.Compose(
                        Vector3.OneReadOnly,
                        Quaternion.RotationAxis(new Vector3(0, 0, -1), angle),
                        Vector3.ZeroReadOnly
                    );
                    const curveTransform = pivotMatrix.clone().invert();
                    curveTransform.multiplyToRef(rotMatrix, curveTransform);
                    curveTransform.multiplyToRef(pivotMatrix, curveTransform);
                    curveTransform.setTranslation(
                        curveTransform.getTranslation().addInPlaceFromFloats(-generatedVertexData.center, 0, 0)
                    );

                    transform.copyFrom(curveTransform);
                }

                transform.multiplyToRef(scaleMatrix, transform);
                generatedVertexData.data.transform(transform);
            }

            vertexData.copyFrom(generatedVertices[0].data);

            if (generatedVertices.length > 1) {
                vertexData.merge(generatedVertices.slice(1).map(generatedVertexData => generatedVertexData.data));
            }
        }

        // const calcW = this.textMeshFont.font.getAdvanceWidth(text) * GLYPH_COORDS_SCALE;

        // if (vertexData.positions) {
        //     const max = Vector3.Zero();
        //     for (let i = 0; i < vertexData.positions.length; i += 3) {
        //         const p = vertexData.positions;
        //         max.x = Math.max(max.x, p[i]);
        //         max.y = Math.max(max.y, p[i + 1]);
        //         max.z = Math.max(max.z, p[i + 2]);
        //     }
        //     const centerTransform = Matrix.Compose(Vector3.OneReadOnly, Quaternion.Identity(), max.scale(-0.5));
        //     vertexData.transform(centerTransform);
        // }

        return vertexData;
    }
}
